import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Users, Send, Loader2, Trash2, Download, RefreshCw, Search } from 'lucide-react';
import { format } from 'date-fns';

interface Subscriber {
  id: string;
  email: string;
  is_verified: boolean;
  subscribed_at: string | null;
  unsubscribed_at: string | null;
}

export default function NewsletterManagement() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('status_subscriptions')
        .select('*')
        .order('subscribed_at', { ascending: false });

      if (error) throw error;
      setSubscribers(data || []);
    } catch (err: any) {
      toast.error('Failed to load subscribers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubscribers(); }, []);

  const activeSubscribers = subscribers.filter(s => !s.unsubscribed_at);
  const unsubscribed = subscribers.filter(s => !!s.unsubscribed_at);

  const filteredSubscribers = searchQuery.trim()
    ? subscribers.filter(s => s.email.toLowerCase().includes(searchQuery.toLowerCase()))
    : subscribers;

  const handleRemoveSubscriber = async (id: string) => {
    if (!window.confirm('Remove this subscriber permanently?')) return;
    try {
      const { error } = await supabase
        .from('status_subscriptions')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setSubscribers(prev => prev.filter(s => s.id !== id));
      toast.success('Subscriber removed.');
    } catch (err: any) {
      toast.error('Failed to remove: ' + err.message);
    }
  };

  const handleExportCSV = () => {
    const rows = [
      ['Email', 'Subscribed At', 'Status'],
      ...activeSubscribers.map(s => [
        s.email,
        s.subscribed_at ? format(new Date(s.subscribed_at), 'yyyy-MM-dd HH:mm') : '',
        s.unsubscribed_at ? 'Unsubscribed' : 'Active',
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported ' + activeSubscribers.length + ' subscribers.');
  };

  const handleSendNewsletter = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Please fill in both subject and body.');
      return;
    }
    if (activeSubscribers.length === 0) {
      toast.error('No active subscribers to send to.');
      return;
    }
    if (!window.confirm(`Send newsletter to ${activeSubscribers.length} subscribers?`)) return;

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-newsletter', {
        body: {
          subject: subject.trim(),
          body: body.trim(),
          recipients: activeSubscribers.map(s => s.email),
        },
      });

      if (error) throw error;

      if (data?.failed > 0) {
        toast.warning(`Sent to ${data.sent} subscribers, ${data.failed} failed.`);
      } else {
        toast.success(`Newsletter sent to ${data?.sent || activeSubscribers.length} subscribers!`);
      }
      setSubject('');
      setBody('');
    } catch (err: any) {
      toast.error('Failed to send: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeSubscribers.length}</p>
                <p className="text-xs text-muted-foreground">Active Subscribers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Mail className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{subscribers.length}</p>
                <p className="text-xs text-muted-foreground">Total Signups</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <Trash2 className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{unsubscribed.length}</p>
                <p className="text-xs text-muted-foreground">Unsubscribed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="subscribers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="subscribers" className="flex items-center gap-2">
            <Users className="h-4 w-4" /> Subscribers
          </TabsTrigger>
          <TabsTrigger value="compose" className="flex items-center gap-2">
            <Send className="h-4 w-4" /> Compose Newsletter
          </TabsTrigger>
        </TabsList>

        <TabsContent value="subscribers">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Subscriber List</CardTitle>
                  <CardDescription>Manage your newsletter subscribers</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchSubscribers}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportCSV}>
                    <Download className="h-4 w-4 mr-1" /> Export CSV
                  </Button>
                </div>
              </div>
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredSubscribers.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  {searchQuery ? 'No subscribers match your search.' : 'No subscribers yet.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Subscribed</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubscribers.map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell className="font-medium">{sub.email}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {sub.subscribed_at
                            ? format(new Date(sub.subscribed_at), 'MMM d, yyyy')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {sub.unsubscribed_at ? (
                            <Badge variant="secondary">Unsubscribed</Badge>
                          ) : (
                            <Badge className="bg-green-500/10 text-green-600 border-green-200">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRemoveSubscriber(sub.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compose">
          <Card>
            <CardHeader>
              <CardTitle>Compose Newsletter</CardTitle>
              <CardDescription>
                Write and send a newsletter to {activeSubscribers.length} active subscriber{activeSubscribers.length !== 1 ? 's' : ''}.
                The body supports Markdown formatting.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Subject</label>
                <Input
                  placeholder="e.g. NexQR Monthly Update — January 2026"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Body (Markdown supported)</label>
                <Textarea
                  placeholder="Write your newsletter content here...&#10;&#10;# Heading&#10;&#10;Hello subscribers! Here's what's new..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={16}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  Will be sent to <strong>{activeSubscribers.length}</strong> active subscribers.
                  {!activeSubscribers.length && ' Add subscribers first.'}
                </p>
                <Button
                  onClick={handleSendNewsletter}
                  disabled={sending || !subject.trim() || !body.trim() || activeSubscribers.length === 0}
                >
                  {sending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Send Newsletter</>
                  )}
                </Button>
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <h4 className="text-sm font-bold mb-2">Formatting Tips</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  The body supports basic Markdown: <strong># Heading</strong>, <strong>## Subheading</strong>, <strong>**bold text**</strong>, 
                  and <strong>- list items</strong>. Separate paragraphs with a blank line. 
                  Emails are sent individually to each subscriber via SMTP.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
